import { TexttemplateModel } from 'projects/core/src/lib/models/texttemplate.model';
import { Subscription } from 'rxjs';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core'
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { MenuItem } from 'primeng/api';
import { LanguageModel } from 'projects/core/src/lib/models/language.model';
import { TextblockModel } from 'projects/core/src/lib/models/textblock.model';
import { BaseForm } from '../../../classes/base-form';
import { UiService } from '../../../services/ui.service';
import { ApiService } from '../../../services/api.service';
import { HelperService } from '../../../services/helper.service';
import { TextcontentModel } from 'projects/core/src/lib/models/textcontent.model';
import { FormHelper } from '../../../shared/form-helper';
import { PillItem } from '../../../shared/pill-nav/pill-nav.component';
import { CustomValidators } from 'projects/core/src/lib/validators/custom.validators';

export enum TextContentModes {
  Textblock = 'textblocks',
  Texttemplate = 'texttemplates',
  Messageorder = 'messageorders',
}

@Component({
  selector: 'app-content-editor',
  templateUrl: './content-editor.component.html',
  styleUrls: ['./content-editor.component.scss']
})
export class ContentEditorComponent extends BaseForm implements OnInit, OnChanges, OnDestroy {

  @Input() owner: TexttemplateModel | TextblockModel = null;
  @Input() languages: Array<LanguageModel> = [];
  @Input() mode: TextContentModes = null;
  @Input() template: string = null;

  @Output() setEdit = new EventEmitter<string>();

  public selectedLanguage: LanguageModel = null;
  public navEntries: Array<PillItem> = [];
  public subscriptions: Array<Subscription> = [];
  public items: Array<MenuItem> = [];

  public previewVisible = false;
  public previewLoading = false;
  public previewContent: string = null;

  public sendVisible = false;
  public sendSending = false;

  public editRight = null;

  public form = this.fb.group({
    title: new UntypedFormControl(''),
    content: new UntypedFormControl(''),
  });

  public sendForm = this.fb.group({
    recipient: new UntypedFormControl('', [Validators.required, CustomValidators.email()]),
  });

  private contents: {[key: string]: { title?: string, content: string }} = {};

  constructor(
    private fb: UntypedFormBuilder,
    private ui: UiService,
    private api: ApiService,
    private helper: HelperService,
    public transloco: TranslocoService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.buildMenu();
    this.ui.setFocus();
    this.subscriptions.push(this.form.valueChanges.subscribe((value: any) => {
      if (this.selectedLanguage !== null) {
        this.contents[this.selectedLanguage.key].title = value.title;
        this.contents[this.selectedLanguage.key].content = value.content;
        this.setNavContentState();
      }
    }));
    switch (this.mode) {
      case(TextContentModes.Textblock): {
        this.editRight = 'admin.textblock.edit';
        break;
      }
      case(TextContentModes.Messageorder): {
        this.editRight = 'admin.messageorder.edit';
        break;
      }
      default: {
        this.editRight = 'admin.texttemplate.edit';
        break;
      }
    }
  }

  ngOnChanges(): void {
    this.buildNavItems();
    this.mapContents();
  }

  ngOnDestroy(): void {
    this.helper.unsetSubscriptions(this.subscriptions);
  }

  public onSubmit(): void {
    this.ui.hideMessage();
    if (this.form.valid === true && this.mode !== null) {
      this.submitting = true;
      this.api.saveTextContents(this.mode, this.owner.id, this.getPayload()).subscribe({
        next: (result: any) => {
          this.owner.deserialize(result.data);
          this.ui.toastSuccess(this.transloco.translate('common.messages.saved', { value: this.owner.identifier }), this.transloco.translate('common.saved'));
          this.stopEdit();
        },
        error: (error: any) => {
          this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
          FormHelper.setBackendErrors(this.form, error.fielderrors);
        }
      }).add(() => {
        this.submitting = false;
      });
    } else {
      this.ui.showMessage('error', this.transloco.translate('common.error_on_form_please_correct'), this.transloco.translate('common.error'));
      FormHelper.markFormAsDirty(this.form);
    }
  }

  protected startEdit(): void {
    this.setEdit.emit('contents');
    super.startEdit();
    if (this.owner !== null) {
      this.setFormValue();
    }
    this.ui.setFormFocus();
  }

  protected stopEdit(): void {
    this.setEdit.emit(null);
    super.stopEdit();
    this.ui.setFocus();
    this.mapContents();
  }

  public getEditorInit(): any {
    let language = 'en';
    let language_url = null;
    if (this.transloco.getActiveLang() === 'de') {
      language = 'de';
      language_url = 'assets/tiny/langs/de.js';
    }
    return {
      // height: 'calc(100% - 20px)',
      menubar: false,
      plugins: [
        'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview', 'anchor',
        'searchreplace', 'visualblocks', 'code', 'fullscreen',
        'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
      ],
      toolbar: 'undo redo | blocks | bold italic backcolor | \
        alignleft aligncenter alignright alignjustify | \
        bullist numlist outdent indent | link | removeformat | code',
      language,
      language_url
    };
  }

  public onSelectLanguage(entry: PillItem): void {
    this.selectedLanguage = this.languages.find((language: LanguageModel) => {
      return language.id === entry.id;
    });
    this.setFormValue();
  }

  protected getPayload(): any {
    const payload: any = {};
    Object.keys(this.contents).forEach((key: string) => {
      if (this.contents[key]?.title || this.contents[key]?.content) {
        payload[key] = this.contents[key];
      }
    });
    return payload;
  }

  protected setFormValue(): void {
    if (this.inEdit === true) {
      const key = this.selectedLanguage?.key;
      if (key) {
        this.form.patchValue({
          title: this.contents[key]?.title || '',
          content: this.contents[key]?.content || '',
        });
      }
    }
  }

  protected mapContents(): void {
    if (this.languages?.length > 0 && this.owner !== null) {
      this.languages.forEach((language: LanguageModel) => {
        const content = this.owner.contents.find((content: TextcontentModel) => {
          return content.language?.id === language.id;
        });
        this.contents[language.key] = {
          title: content?.title || '',
          content: content?.content || '',
        };
      });
    }
    this.setNavContentState();
  }

  protected buildNavItems(): void {
    if (this.languages.length > 0) {
      this.navEntries = this.languages.map((language: LanguageModel): PillItem => {
        return {
          id: language.id,
          label: language.key,
        };
      });
      this.selectedLanguage = this.languages[0];
    }
    this.setNavContentState();
  }

  protected setNavContentState(): void {
    this.navEntries.forEach((entry: PillItem) => {
      entry.hasContent = this.contents[entry.label]?.title !== '' || this.contents[entry.label]?.content !== '';
    });
  }

  protected onShowPreview(): void {
    if (this.owner !== null && this.selectedLanguage !== null) {
      this.previewVisible = true;
      this.previewLoading = true;
      this.api.showTexttemplatePreview(this.mode, this.owner.id, this.selectedLanguage.key).subscribe({
        next: (result: any) => {
          this.previewContent = result.data;
          this.previewLoading = false;
        },
        error: (error: any) => {},
      });
    }
  }

  protected onOpenSendPreview(): void {
    if (this.owner !== null && this.selectedLanguage !== null) {
      this.sendVisible = true;
    }
  }

  public onSendPreview(): void {
    if (this.sendForm.valid) {
      this.sendSending = true;
      const recipient = this.sendForm.get('recipient').value;
      this.api.sendTexttemplatePreview(this.mode, this.owner.id, this.selectedLanguage.key, recipient).subscribe({
        next: () => {
          this.ui.toastSuccess(this.transloco.translate('common.messages.preview_sended', { value: this.owner.identifier }), this.transloco.translate('common.labels.sended'));
        },
        error: (error: any) => {},
      }).add(() => {
        this.sendVisible = false;
        this.sendSending = false;
      });
    } else {
      FormHelper.markFormAsDirty(this.sendForm);
    }
  }

  public isTextBlockMode(): boolean {
    return this.mode === TextContentModes.Textblock;
  }

  protected buildMenu(): void {
    this.items = [];
    if (this.selectedLanguage !== null && !this.isTextBlockMode()) {
      this.items.push({
        label: this.transloco.translate('common.labels.show_preview'),
        icon: 'pi pi-eye',
        command: () => {
          this.onShowPreview();
        }
      });
      if (this.owner.type !== 'text') {
        this.items.push({
          label: this.transloco.translate('common.labels.send_preview'),
          icon: 'pi pi-envelope',
          command: () => {
            this.onOpenSendPreview();
          }
        });
      }
    }
  }

}
